import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { User, LogOut, X, LogIn, Menu } from 'lucide-react';
import { loadAvatar } from '../UtilityComponents/AvatarService';

function EnhancedHeader() {
  const [loggedIn, setIsLoggedIn] = useState(false);
  const [logeduser, setLogeduser] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatar, setAvatar] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Lock body scroll when mobile menu is open
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileMenuOpen]);

  // Check login status on mount and when storage changes
  const checkLoginStatus = async () => {
    const storedUser = localStorage.getItem('loggedInUser');
    if (storedUser) {
      setLogeduser(storedUser);
      setIsLoggedIn(true);
      const avatarData = await loadAvatar(storedUser);
      setAvatar(avatarData || '');
    } else {
      setLogeduser('');
      setIsLoggedIn(false);
      setAvatar('');
    }
  };

  useEffect(() => {
    checkLoginStatus();

    // Listen for storage changes (login/logout from other tabs or components)
    const handleStorageChange = () => {
      checkLoginStatus();
    };

    // Listen for avatar changes
    const handleAvatarChange = async () => {
      const storedUser = localStorage.getItem('loggedInUser');
      if (storedUser) {
        const avatarData = await loadAvatar(storedUser);
        setAvatar(avatarData || '');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // Custom event for same-tab updates
    window.addEventListener('loginStateChange', handleStorageChange);
    window.addEventListener('avatarChange', handleAvatarChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginStateChange', handleStorageChange);
      window.removeEventListener('avatarChange', handleAvatarChange);
    };
  }, []);

  const handleLogout = () => {
    setIsLoggedIn(false);
    setLogeduser('');
    localStorage.removeItem('loggedInUser');
    localStorage.removeItem('authToken');
    setMobileMenuOpen(false);
    window.dispatchEvent(new Event('loginStateChange'));
    navigate('/');
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className="header-enhanced">
        <nav className="nav-container">
          {/* Desktop Navigation */}
          <div className="desktop-nav-wrapper">
            <div className="logo-container">
              <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Logo" className="header-logo" width="70" height="70" loading="eager" />
            </div>
            <ul className="nav-list desktop-nav">
              <li><NavLink to="/" className="nav-link">Αρχική</NavLink></li>
            <li><NavLink to="/practice" className="nav-link">Εξάσκηση</NavLink></li>
            <li><NavLink to="/exams" className="nav-link">Προσομοίωση Εξετάσεων</NavLink></li>
            {loggedIn && <li><NavLink to="/statistics" className="nav-link">Στατιστικά</NavLink></li>}
            <li><NavLink to="/info" className="nav-link">Πληροφορίες</NavLink></li>
            <li><NavLink to="/contact" className="nav-link">Επικοινωνία</NavLink></li>
          </ul>
          
          <div className="nav-actions">
            {!loggedIn ? (
              <Button
                onClick={() => navigate('/login')}
                variant="ghost"
                className="login-button"
                style={{ 
                  height: '38px', 
                  minHeight: '38px', 
                  maxHeight: '38px',
                  padding: '0.5rem 0.875rem',
                  fontSize: '14px',
                  background: '#fff',
                  color: '#333',
                  border: 'none'
                }}
              >
                <LogIn className="icon" /> Σύνδεση
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="ghost"
                  className="user-button"
                  style={{ 
                    height: '38px', 
                    minHeight: '38px', 
                    maxHeight: '38px',
                    padding: '0.5rem 0.875rem',
                    fontSize: '14px',
                    background: '#fff',
                    color: '#333',
                    border: 'none'
                  }}
                >
                  {avatar ? (
                    <img src={avatar} alt="Avatar" className="user-avatar" />
                  ) : (
                    <User className="icon" />
                  )}
                  Dashboard του {logeduser}
                </Button>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="logout-button"
                  style={{ 
                    height: '38px', 
                    minHeight: '38px', 
                    maxHeight: '38px',
                    padding: '0.5rem 0.875rem',
                    fontSize: '14px',
                    background: '#fff',
                    color: '#333',
                    border: 'none'
                  }}
                >
                  <LogOut className="icon" /> Αποσύνδεση
                </Button>
              </>
            )}
          </div>
          </div>

          {/* Mobile Navigation */}
          <div className="mobile-nav">
            <div className="mobile-header">
              <div className="mobile-logo-container">
                <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Logo" className="mobile-header-logo" width="40" height="40" loading="eager" />
                <span className="mobile-logo">ΙΕΚ Εξάσκηση</span>
              </div>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="burger-button-simple"
                aria-label="Toggle menu"
              >
                <Menu style={{ width: '24px', height: '24px' }} />
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Portal - Render outside header */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-panel" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <h2 className="mobile-menu-title">Μενού</h2>
              <button onClick={() => setMobileMenuOpen(false)} className="close-button">
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>
            
            <div className="mobile-menu-items">
              <NavLink to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
                Αρχική
              </NavLink>
              <NavLink to="/practice" className="mobile-nav-link" onClick={closeMobileMenu}>
                Εξάσκηση
              </NavLink>
              <NavLink to="/exams" className="mobile-nav-link" onClick={closeMobileMenu}>
                Προσομοίωση Εξετάσεων
              </NavLink>
              {loggedIn && (
                <NavLink to="/statistics" className="mobile-nav-link" onClick={closeMobileMenu}>
                  Στατιστικά
                </NavLink>
              )}
              <NavLink to="/info" className="mobile-nav-link" onClick={closeMobileMenu}>
                Πληροφορίες
              </NavLink>
              <NavLink to="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>
                Επικοινωνία
              </NavLink>

              <div className="mobile-divider"></div>

              {!loggedIn ? (
                <Button
                  onClick={() => {
                    navigate('/login');
                    closeMobileMenu();
                  }}
                  variant="default"
                  className="mobile-action-button login-button"
                >
                  <LogIn className="icon" /> Σύνδεση
                </Button>
              ) : (
                <>
                  <div className="mobile-user-info">
                    {avatar ? (
                      <img src={avatar} alt="Avatar" className="user-avatar" />
                    ) : (
                      <User className="icon" />
                    )}
                    <span>{logeduser}</span>
                  </div>
                  <Button
                    onClick={() => {
                      navigate('/dashboard');
                      closeMobileMenu();
                    }}
                    variant="outline"
                    className="mobile-action-button"
                  >
                    <User className="icon" /> Dashboard
                  </Button>
                  <Button
                    onClick={handleLogout}
                    variant="destructive"
                    className="mobile-action-button"
                  >
                    <LogOut className="icon" /> Αποσύνδεση
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EnhancedHeader;
